import { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes, faCopy, faPause, faPlay, faBan, faRotateLeft,
  faPlus, faTrash, faFloppyDisk, faCircleInfo, faTriangleExclamation,
  faSliders, faKey,
} from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import ActionButton from "../ui/ActionButton";
import Toggle from "../ui/Toggle";
import FormattedDate from "../ui/FormattedDate";
import LoadingSpinner from "../ui/LoadingSpinner";
import {
  getPartnershipPrefs, updatePartnershipPrefs,
  getDonorLimits, updateDonorLimits,
} from "../../api/partnershipsApi";
import "../../style/PartnershipDetailPanel.css";

function PartnershipDetailPanel({
  partnership,
  side,
  closing,
  onClose,
  onAction,
  onCopyKey,
  categories,
  materialTypes,
  onSnack,
}) {
  const { t } = useTranslation();
  const [editingRows, setEditingRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [saving, setSaving] = useState(false);

  const p = partnership;

  const materialsForCategory = useCallback((cat) => {
    return materialTypes
      .filter((m) => m.material_category === cat)
      .map((m) => m.material_name)
      .filter(Boolean)
      .sort();
  }, [materialTypes]);

  useEffect(() => {
    if (!partnership) return;
    const load = async () => {
      setLoadingRows(true);
      try {
        if (side === "donor") {
          const res = await getDonorLimits(partnership.id);
          setEditingRows(res.limits || []);
        } else {
          const res = await getPartnershipPrefs(partnership.id);
          setEditingRows(res.prefs || []);
        }
      } catch {
        setEditingRows([]);
      } finally {
        setLoadingRows(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnership?.id, side]);

  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const addRow = () => {
    if (side === "donor") {
      setEditingRows((prev) => [...prev, { id: `new-${Date.now()}`, material_category: categories[0] || "", material_name: null, limit_type: "pct", limit_value: 25, is_active: true }]);
    } else {
      setEditingRows((prev) => [...prev, { id: `new-${Date.now()}`, material_category: categories[0] || "", material_name: null, min_qty: null, max_qty: null, is_active: true }]);
    }
  };

  const updateRow = (idx, field, value) => {
    setEditingRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const removeRow = (idx) => {
    setEditingRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (side === "donor") {
        const res = await updateDonorLimits(p.id, editingRows);
        setEditingRows(res.limits || []);
        if (onSnack) onSnack(t("Allocation limits saved"));
      } else {
        const res = await updatePartnershipPrefs(p.id, editingRows);
        setEditingRows(res.prefs || []);
        if (onSnack) onSnack(t("Material preferences saved"));
      }
    } catch (err) {
      if (onSnack) onSnack(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  if (!p) return null;

  const isDonor = side === "donor";
  const partnerName = isDonor ? (p.receiver_username || t("Unclaimed")) : p.donor_username;
  const partnerOrg = isDonor ? p.receiver_org_name : p.donor_org_name;
  const isClaimed = !!p.receiver_user_id;

  return (
    <div className={`pdp-panel${closing ? " pdp-closing" : ""}`}>
      {/* header — single line to match table th height */}
      <div className="pdp-header">
        <div className="pdp-header-title">
          <span className="pdp-partner-name">{partnerName}</span>
          {partnerOrg && <span className="pdp-partner-org">{partnerOrg}</span>}
        </div>
        <button className="pdp-close" onClick={onClose} title={t("Close")}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      {/*
        key={p.id} forces React to unmount/remount this entire subtree
        whenever the partnership changes, guaranteeing the CSS animation fires.
      */}
      <div className="pdp-content" key={p.id}>
        {/* status + actions bar */}
        <div className="pdp-status-bar">
          <span className={`pdp-status-chip pdp-status--${p.status}`}>
            {p.status}
          </span>
          <div className="pdp-status-actions">
            {p.status === "active" && (
              <ActionButton variant="default" iconOnly icon={faPause} tooltip={t("Pause")}
                onClick={() => onAction(p.id, "pause", partnerName)} />
            )}
            {p.status === "paused" && (
              <ActionButton variant="default" iconOnly icon={faPlay} tooltip={t("Resume")}
                onClick={() => onAction(p.id, "resume", partnerName)} />
            )}
            {isDonor && p.status !== "revoked" && p.status !== "expired" && (
              <ActionButton variant="danger" iconOnly icon={faBan} tooltip={t("Revoke")}
                onClick={() => onAction(p.id, "revoke", partnerName)} />
            )}
            {isDonor && p.status === "revoked" && (
              <ActionButton variant="secondary" iconOnly icon={faRotateLeft} tooltip={t("Restore")}
                onClick={() => onAction(p.id, "restore", partnerName)} />
            )}
          </div>
        </div>

        <div className="pdp-body">
          {/* detail grid */}
          <div className="pdp-section">
            <div className="pdp-detail-grid">
              {isDonor && (
                <div className="pdp-detail-item pdp-detail-item--full">
                  <span className="pdp-detail-label">{t("Partnership Key")}</span>
                  <div className="pdp-key-row">
                    <span className="pdp-key-mono">{p.partner_key}</span>
                    {onCopyKey && (
                      <ActionButton variant="default" iconOnly icon={faCopy}
                        tooltip={t("Copy")} onClick={() => onCopyKey(p.partner_key)}
                        flashOnClick />
                    )}
                  </div>
                </div>
              )}
              <div className="pdp-detail-item">
                <span className="pdp-detail-label">{t("Max Allocation")}</span>
                <span className="pdp-alloc-badge">{p.max_listing_pct ?? 25}%</span>
              </div>
              {!isDonor && p.donor_city && (
                <div className="pdp-detail-item">
                  <span className="pdp-detail-label">{t("Donor Location")}</span>
                  <span>{p.donor_city}</span>
                </div>
              )}
              <div className="pdp-detail-item">
                <span className="pdp-detail-label">{t("Created")}</span>
                <FormattedDate date={p.created_at} />
              </div>
              <div className="pdp-detail-item">
                <span className="pdp-detail-label">{t("Claimed")}</span>
                {p.claimed_at ? <FormattedDate date={p.claimed_at} /> : <span className="pdp-muted">{t("Not yet")}</span>}
              </div>
              {isDonor && (
                <div className="pdp-detail-item">
                  <span className="pdp-detail-label">{t("Expires")}</span>
                  <FormattedDate date={p.expires_at} />
                </div>
              )}
              {p.revoked_at && (
                <div className="pdp-detail-item">
                  <span className="pdp-detail-label">{t("Revoked")}</span>
                  <FormattedDate date={p.revoked_at} />
                </div>
              )}
            </div>
          </div>

          {/* notes */}
          {p.notes && (
            <div className="pdp-section">
              <div className="pdp-section-label">{t("Notes")}</div>
              <p className="pdp-notes-text">{p.notes}</p>
            </div>
          )}

          {/* editor */}
          {isClaimed && (p.status === "active" || p.status === "paused") && (
            <div className="pdp-section">
              <div className="pdp-section-header">
                <div className="pdp-section-label">
                  <FontAwesomeIcon icon={isDonor ? faKey : faSliders} style={{ marginRight: 6, fontSize: "0.65rem" }} />
                  {isDonor ? t("Allocation Limits") : t("Material Preferences")}
                </div>
                <Tooltip title={isDonor
                  ? t("Set per-category limits on how much of each listing to allocate to this partner.")
                  : t("Define which materials to auto-request, with optional quantity ranges.")}
                  arrow placement="top">
                  <span className="pdp-info-icon"><FontAwesomeIcon icon={faCircleInfo} /></span>
                </Tooltip>
              </div>

              {loadingRows ? (
                <div className="pdp-loading"><LoadingSpinner size={22} /></div>
              ) : (
                <>
                  {editingRows.length > 0 ? (
                    <div className="pdp-editor-rows">
                      {editingRows.map((row, idx) => (
                        <div className="pdp-editor-row" key={row.id || idx}>
                          <div className="pdp-editor-field">
                            <label className="pdp-field-label">{t("Category")}</label>
                            <select className="pdp-select" value={row.material_category || ""}
                              onChange={(e) => updateRow(idx, "material_category", e.target.value)}>
                              <option value="">{t("Select...")}</option>
                              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                          </div>
                          <div className="pdp-editor-field">
                            <label className="pdp-field-label">{t("Material")}</label>
                            <select className="pdp-select" value={row.material_name || ""}
                              onChange={(e) => updateRow(idx, "material_name", e.target.value || null)}>
                              <option value="">{t("All")}</option>
                              {materialsForCategory(row.material_category).map((m) => (
                                <option key={m} value={m}>{m}</option>
                              ))}
                            </select>
                          </div>

                          {isDonor ? (
                            <>
                              <div className="pdp-editor-field pdp-editor-field--narrow">
                                <label className="pdp-field-label">{t("Type")}</label>
                                <select className="pdp-select" value={row.limit_type || "pct"}
                                  onChange={(e) => updateRow(idx, "limit_type", e.target.value)}>
                                  <option value="pct">%</option>
                                  <option value="lbs">lbs</option>
                                </select>
                              </div>
                              <div className="pdp-editor-field pdp-editor-field--narrow">
                                <label className="pdp-field-label">{t("Limit")}</label>
                                <div className="pdp-input-wrap">
                                  <input type="number" className="pdp-input" value={row.limit_value ?? ""}
                                    onChange={(e) => updateRow(idx, "limit_value", e.target.value ? Number(e.target.value) : "")}
                                    min={row.limit_type === "pct" ? 1 : 0}
                                    max={row.limit_type === "pct" ? 100 : undefined} />
                                  {row.limit_type === "pct" && row.limit_value > 50 && (
                                    <Tooltip title={t("Allocating over 50% may limit availability for others")} arrow>
                                      <span className="pdp-warn-icon"><FontAwesomeIcon icon={faTriangleExclamation} /></span>
                                    </Tooltip>
                                  )}
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="pdp-editor-field pdp-editor-field--narrow">
                                <label className="pdp-field-label">{t("Min (lbs)")}</label>
                                <input type="number" className="pdp-input" value={row.min_qty ?? ""} placeholder="—"
                                  onChange={(e) => updateRow(idx, "min_qty", e.target.value ? Number(e.target.value) : null)} min={0} />
                              </div>
                              <div className="pdp-editor-field pdp-editor-field--narrow">
                                <label className="pdp-field-label">{t("Max (lbs)")}</label>
                                <input type="number" className="pdp-input" value={row.max_qty ?? ""} placeholder="—"
                                  onChange={(e) => updateRow(idx, "max_qty", e.target.value ? Number(e.target.value) : null)} min={0} />
                              </div>
                            </>
                          )}

                          <div className="pdp-editor-field pdp-editor-field--toggle">
                            <label className="pdp-field-label">{t("Active")}</label>
                            <Toggle checked={row.is_active !== false}
                              onChange={(val) => updateRow(idx, "is_active", val)} />
                          </div>
                          <div className="pdp-editor-field pdp-editor-field--action">
                            <ActionButton variant="danger" iconOnly icon={faTrash}
                              tooltip={t("Remove")} onClick={() => removeRow(idx)} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="pdp-empty-text">
                      {isDonor
                        ? t("No limits set — all materials allocated without caps.")
                        : t("No preferences set — add at least one to start auto-receiving.")}
                    </p>
                  )}

                  <div className="pdp-editor-actions">
                    <ActionButton variant="secondary" icon={faPlus} onClick={addRow}>
                      {isDonor ? t("Add Limit") : t("Add Preference")}
                    </ActionButton>
                    <ActionButton variant="primary" icon={faFloppyDisk} onClick={handleSave} loading={saving}>
                      {t("Save")}
                    </ActionButton>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PartnershipDetailPanel;
